import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import PrivateRoute from 'components/Navigation/ProtectedRoute';
import OutlineButton from 'components/OutlineButton';
import SerieModalContent from './ActionModalContent';
import { showErrorToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';
import Table from 'components/Table';
import Modal from 'components/Modal';

import { ISerie } from 'utils/interfaces';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import { DOC_TYPES } from 'utils/constants';

export default function SeriesPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState<Array<ISerie>>([]);

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionData, setActionData] = useState<ISerie | undefined>(undefined);

  useEffect(() => {
    const getSeries = async () => {
      setLoading(true);
      const result = await generalApi.get('/series');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setSeries(result.data);
      setLoading(false);
    };
    if (refresh) {
      getSeries();
      setRefresh(false);
    }
  }, [generalApi, refresh]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal
          isOpen={isActionOpen}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <SerieModalContent
            title={actionData ? 'Editar serie' : 'Crear serie'}
            editData={actionData}
            onClose={() => {
              setIsActionOpen(false);
              setActionData(undefined);
            }}
            onSuccess={() => {
              setRefresh(true);
              setIsActionOpen(false);
              setActionData(undefined);
            }}
          />
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Lista de Series</p>
            <div className="space-y-2 sm:ml-20 sm:flex sm:space-y-0 sm:space-x-4">
              <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => setIsActionOpen(true)}>
                Nueva Serie
              </OutlineButton>
            </div>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando series"
            columns={[
              {
                title: 'Tipo',
                dataKey: 'doc_type',
                render: (item: ISerie) => DOC_TYPES[item.doc_type],
              },
              {
                title: 'Serie',
                dataKey: 'serie',
              },
              {
                title: 'Número',
                dataKey: 'number',
              },
              {
                title: 'Comentario',
                dataKey: 'comment',
              },
              {
                title: 'Opciones',
                dataKey: 'options',
                disableSort: true,
                render: (item: ISerie) => (
                  <div className="flex space-x-2 pr-2">
                    <SolidButton
                      className="w-auto"
                      color={BUTTON_COLORS.BLUE}
                      onClick={() => {
                        setIsActionOpen(true);
                        setActionData(item);
                      }}
                    >
                      Editar
                    </SolidButton>
                  </div>
                ),
              },
            ]}
            data={series}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
